import React, { useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Minimize2 } from "lucide-react";
import { apiBaseUrl, authHeaders } from "../pages/dispatcher/shared";
import LoadingState from "../pages/dispatcher/LoadingState";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";

// Deadhead-to-pickup map. Historical actual GPS breadcrumb trail (grey) from
// deadhead start -> current truck position, then a DASHED straight-line from
// current -> pickup (remaining, explicitly straight-line, not a routed path).

const DEADHEAD_COLOR = "#6366f1";
const REMAINING_COLOR = "#94a3b8";

function truckIcon(label) {
  const html = `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
    <div style="background:#6366f1;color:#fff;border:1px solid #0b1020;font-size:11px;font-weight:800;padding:2px 8px;border-radius:999px;white-space:nowrap;margin-bottom:2px;box-shadow:0 2px 8px rgba(0,0,0,.5);">${label || "truck"}</div>
    <svg width="42" height="42" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.5));">
      <rect x="1" y="9" width="14" height="8" rx="1.5" fill="#6366f1" stroke="#0b1020" stroke-width="1.2"/>
      <path d="M15 11 h6 l3 4 v2 h-9 z" fill="#6366f1" stroke="#0b1020" stroke-width="1.2"/>
      <circle cx="6" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
      <circle cx="20" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
    </svg></div>`;
  return L.divIcon({ html, className: "", iconSize: [96, 64], iconAnchor: [48, 52] });
}
function pin(color, letter) {
  const html = `<svg width="26" height="34" viewBox="0 0 26 34" style="filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));">
    <path d="M13 0C5.8 0 0 5.8 0 13c0 9.7 13 21 13 21s13-11.3 13-21C26 5.8 20.2 0 13 0z" fill="${color}"/>
    <circle cx="13" cy="13" r="7" fill="#fff"/><text x="13" y="17" font-size="10" font-weight="800" text-anchor="middle" fill="${color}">${letter}</text></svg>`;
  return L.divIcon({ html, className: "", iconSize: [26, 34], iconAnchor: [13, 34] });
}
function Fit({ points }) {
  const map = useMap();
  const done = React.useRef(false);
  useEffect(() => {
    if (done.current || !points || points.length < 1) return;
    done.current = true;
    if (points.length === 1) map.setView(points[0], 9);
    else map.fitBounds(L.latLngBounds(points), { padding: [55, 55], maxZoom: 11 });
  }, [points, map]);
  return null;
}

export default function EnRouteMapModal({ truckId, nextLoadId, unitNumber, driverName, pickupLabel, onClose }) {
  const tileUrl = useTileLayerUrl();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setError(null);
    axios.get(`${apiBaseUrl}/fleet/en-route-to-pickup/trail`, { ...authHeaders(), params: { truck_id: truckId, next_load_id: nextLoadId } })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [truckId, nextLoadId]);

  const trail = data?.deadhead_trail || [];
  const current = data?.current;
  const pickup = data?.pickup;
  const meta = data?.meta || {};
  const pts = [...trail.map((p) => [p.lat, p.lng]), ...(pickup ? [[pickup.lat, pickup.lng]] : [])];
  const center = pts[0] || [39.5, -98.35];

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ width: "92vw", height: "88vh", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
            Deadhead to pickup — Truck {unitNumber || "?"}
            <span style={{ fontWeight: 400, color: "var(--muted)", marginLeft: 8 }}>{driverName || "—"}{pickupLabel ? ` · → ${pickupLabel}` : ""}</span>
          </div>
          <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}><Minimize2 size={13} /> Close</button>
        </div>
        {loading && <LoadingState label="Loading route…" />}
        {error && <div style={{ color: "#e0745d", padding: 20, fontSize: 13 }}>Couldn't load the route: {error}</div>}
        {!loading && !error && data && (
          <>
            <div style={{ display: "flex", gap: 18, marginBottom: 8, fontSize: 12, color: "var(--muted)", flexWrap: "wrap" }}>
              <span style={{ color: DEADHEAD_COLOR, fontWeight: 700 }}>Deadhead driven {Math.round(meta.deadhead_driven_miles ?? 0).toLocaleString()} mi</span>
              <span>{meta.gps_points ?? 0} GPS points</span>
              <span style={{ display: "flex", alignItems: "center", gap: 14, marginLeft: "auto", flexWrap: "wrap" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ display: "inline-block", width: 16, height: 3, borderRadius: 2, background: DEADHEAD_COLOR }} /> Driven (actual GPS)</span>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ display: "inline-block", width: 16, height: 0, borderTop: `3px dashed ${REMAINING_COLOR}` }} /> Remaining (straight‑line)</span>
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)" }}>
              <MapContainer center={center} zoom={7} style={{ height: "100%", width: "100%" }}>
                {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
                <Fit points={pts} />
                {trail.length >= 2 && <Polyline positions={trail.map((p) => [p.lat, p.lng])} pathOptions={{ color: DEADHEAD_COLOR, weight: 4 }} />}
                {current && pickup && <Polyline positions={[[current.lat, current.lng], [pickup.lat, pickup.lng]]} pathOptions={{ color: REMAINING_COLOR, weight: 3, opacity: 0.8, dashArray: "8 7" }} />}
                {trail[0] && <Marker position={[trail[0].lat, trail[0].lng]} icon={pin("#64748b", "S")}><Tooltip>Deadhead start</Tooltip></Marker>}
                {current && <Marker position={[current.lat, current.lng]} icon={truckIcon(unitNumber)}><Tooltip>Current position</Tooltip></Marker>}
                {pickup && <Marker position={[pickup.lat, pickup.lng]} icon={pin("#2563eb", "B")}><Tooltip>Pickup{pickup.label ? ` — ${pickup.label}` : ""}</Tooltip></Marker>}
              </MapContainer>
            </div>
            {trail.length === 0 && <div style={{ color: "var(--muted)", padding: "10px 2px", fontSize: 12.5 }}>No deadhead breadcrumbs recorded yet.</div>}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
